import { appConfig } from "@/constants";

const BASE_URI = appConfig.IMMUTABLE_BASE_URI;

type Opts = { campaignId?: number };

const buildUrl = (
  path: string,
  params?: Record<string, string | number | undefined>,
) => {
  const url = new URL(`${BASE_URI}${path}`);
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && String(value).length > 0) {
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
};

/**
 * Referral code response structure
 */
export type ReferralCode = {
  referral_code: string;
};

export type ReferralStats = {
  total_accepted_referrals: number;
  total_sweepstake_bonus_distributed: number;
  total_sweepstake_bonus_collected: number;
  total_sweepstake_gems_bonus_collected: number;
  total_sweepstake_gems_bonus_to_be_claimed: number;
};

/**
 * Get user referral stats
 * This endpoint will return the user's referral stats
 *
 * @param accessToken - The user's access token for authentication
 * @returns Promise with the user's referral stats
 */
export async function getUserReferralStats(
  accessToken: string,
): Promise<ReferralStats> {
  try {
    const response = await fetch(`${BASE_URI}/v2/referral/stats`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get referral code: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching referral code:", error);
    throw error;
  }
}

/**
 * Get user referral code
 * This endpoint will return existing code or create a new one if it doesn't exist
 *
 * @param accessToken - The user's access token for authentication
 * @param opts - Optional configuration including campaignId
 * @returns Promise with the user's referral code
 */
export async function getUserReferralCode(
  accessToken: string,
  opts?: Opts,
): Promise<ReferralCode> {
  try {
    const response = await fetch(
      buildUrl("/v2/referral/code", { campaign_id: opts?.campaignId }),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to get referral code: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching referral code:", error);
    throw error;
  }
}

/**
 * Accept a referral invitation
 * This endpoint links the current user to the referrer
 *
 * @param accessToken - The user's access token for authentication
 * @param referrerCode - The referral code from the person who invited them
 * @returns Promise with boolean indicating success
 */
export async function acceptReferral(
  accessToken: string,
  referrerCode: string,
): Promise<boolean> {
  try {
    const response = await fetch(`${BASE_URI}/v2/referral/accept`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        referral_code: referrerCode,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to accept referral: ${response.status}`,
      );
    }

    return true;
  } catch (error) {
    console.error("Error accepting referral:", error);
    throw error;
  }
}
